import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Download, 
  Eye, 
  Upload,
  Calendar,
  User,
  Search,
  Filter,
  ExternalLink
} from 'lucide-react';
import { tenantPortalAPI } from '../services/api';

const TenantDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [error, setError] = useState('');

  const tenantId = localStorage.getItem('tenant_id') || sessionStorage.getItem('tenant_id');
  const isAuthenticated = !!(localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token'));

  // Mock documents for demonstration - in real app this would come from API
  const mockDocuments = [
    {
      id: '1',
      name: 'Lease Agreement - 2024',
      type: 'lease',
      size: '2.4 MB',
      uploadedDate: '2024-01-15T00:00:00Z',
      uploadedBy: 'Property Manager',
      status: 'active',
      description: 'Current lease agreement for unit 2A'
    },
    {
      id: '2', 
      name: 'Move-in Inspection Report',
      type: 'inspection',
      size: '1.8 MB',
      uploadedDate: '2024-01-10T00:00:00Z',
      uploadedBy: 'Property Manager',
      status: 'completed',
      description: 'Pre-move-in condition documentation'
    },
    {
      id: '3',
      name: 'Property Rules & Regulations',
      type: 'policy',
      size: '856 KB',
      uploadedDate: '2024-01-15T00:00:00Z',
      uploadedBy: 'Property Manager',
      status: 'active',
      description: 'Community rules and building policies'
    },
    {
      id: '4',
      name: 'Parking Permit Application',
      type: 'form',
      size: '124 KB',
      uploadedDate: '2024-02-01T00:00:00Z',
      uploadedBy: 'Property Manager',
      status: 'pending',
      description: 'Application for assigned parking space'
    },
    {
      id: '5',
      name: 'Rent Receipt - March 2024',
      type: 'receipt',
      size: '89 KB',
      uploadedDate: '2024-03-01T00:00:00Z',
      uploadedBy: 'System Generated',
      status: 'completed',
      description: 'Payment confirmation for March rent'
    }
  ];

  useEffect(() => {
    if (!isAuthenticated || !tenantId) return;

    const fetchDocuments = async () => {
      try {
        setLoading(true);
        // In a real app, this would fetch tenant-specific documents from the API
        // const docs = await tenantPortalAPI.getDocuments(tenantId);
        
        // Using mock data for now
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
        setDocuments(mockDocuments);
      } catch (error) {
        console.error('Failed to fetch documents:', error);
        setError('Failed to load documents');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [isAuthenticated, tenantId]);

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || doc.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const getTypeColor = (type) => {
    switch (type) {
      case 'lease': return 'bg-blue-100 text-blue-800';
      case 'inspection': return 'bg-green-100 text-green-800';
      case 'policy': return 'bg-purple-100 text-purple-800';
      case 'form': return 'bg-orange-100 text-orange-800';
      case 'receipt': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDownload = (doc) => {
    // In a real app, this would download the actual file
    console.log('Downloading document:', doc.name);
    // For demo purposes, just show an alert
    alert(`Downloading: ${doc.name}\n\nIn a real application, this would download the actual file.`);
  };

  const handleView = (doc) => {
    // In a real app, this would open the document viewer
    console.log('Viewing document:', doc.name);
    alert(`Opening: ${doc.name}\n\nIn a real application, this would open the document viewer.`);
  };

  if (!isAuthenticated) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Please sign in to view your documents.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading documents...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Documents</h1>
          <p className="text-gray-600">Access your lease agreements, receipts, and other important documents</p>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-800">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterType === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterType('all')}
                size="sm"
              >
                All
              </Button>
              <Button
                variant={filterType === 'lease' ? 'default' : 'outline'}
                onClick={() => setFilterType('lease')}
                size="sm"
              >
                Leases
              </Button>
              <Button
                variant={filterType === 'receipt' ? 'default' : 'outline'}
                onClick={() => setFilterType('receipt')}
                size="sm"
              >
                Receipts
              </Button>
              <Button
                variant={filterType === 'form' ? 'default' : 'outline'}
                onClick={() => setFilterType('form')}
                size="sm"
              >
                Forms
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <div className="space-y-4">
        {filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
              <p className="text-gray-600">
                {searchQuery || filterType !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Your documents will appear here once uploaded by property management'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredDocuments.map((doc) => (
            <Card key={doc.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {doc.name}
                        </h3>
                        <Badge className={getTypeColor(doc.type)}>
                          {doc.type}
                        </Badge>
                        <Badge className={getStatusColor(doc.status)}>
                          {doc.status}
                        </Badge>
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-2">{doc.description}</p>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(doc.uploadedDate).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          {doc.uploadedBy}
                        </div>
                        <span>{doc.size}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleView(doc)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleDownload(doc)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Help Section */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-6">
          <div className="flex items-start space-x-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900 mb-2">Need a document?</h3>
              <p className="text-blue-800 text-sm mb-3">
                If you need a specific document that's not listed here, please contact property management. 
                They can upload it to your account or provide it directly.
              </p>
              <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
                <ExternalLink className="h-4 w-4 mr-2" />
                Contact Property Management
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantDocuments;